import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import "./TournamentRegistration.css";

const TournamentRegistration = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [tournamentSettings, setTournamentSettings] = useState({
    tournamentName: "مسابقة ايجنايت للبولينغ",
    date: "13/9/2025",
    dayOfWeek: "يوم السبت",
    time: "6:00 مساءً",
    feePerPlayer: 60,
    playersPerTeam: 2,
    welcomeText:
      "متحمسين نشوفكم وتكونوا جزء من هالتحدي! فرصة حلوة تقضوا وقت ممتع وتتنافسوا على جوائز رهيبة!",
    visible: false,
    registrationRules: [
      "يتثبت التسجيل فور وصول رسوم الاشتراك",
      "التسجيل متاح حتى اكتمال العدد المطلوب",
    ],
    tournamentRules: [
      "الفريق صاحب أعلى نتيجة في كل جولة يتأهل إلى النهائي",
      "كل فريق سيشارك في جولة واحدة فقط ما عدا الجولة النهائية في حال التأهل",
      "يتنافس أفضل فرق في الجولة النهائية لتحديد الفائز",
      "يتم لعب جولة اضافية في حال التعادل",
      "يجب على اللاعبين انتظار دورهم واحترام حدود المسارات",
      "يجب قراءة والالتزام بالارشادات المتواجدة في منطقة البولينغ",
      "أي تصرف غير اخلاقي يؤدي إلى استبعاد اللاعب",
    ],
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  // Initialize form data dynamically based on number of players
  const initializeFormData = (numPlayers) => {
    const data = {
      teamName: "",
      contactNumber: "",
      howDidYouHear: "",
      agreement: false,
    };

    // Add player fields dynamically
    for (let i = 1; i <= numPlayers; i++) {
      data[`player${i}Name`] = "";
      data[`player${i}Age`] = "";
    }

    return data;
  };

  const [formData, setFormData] = useState(initializeFormData(2));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Load tournament settings and check visibility
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, "settings", "tournament"));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          const settings = {
            tournamentName: data.tournamentName || "مسابقة ايجنايت للبولينغ",
            date: data.date || "13/9/2025",
            dayOfWeek: data.dayOfWeek || "يوم السبت",
            time: data.time || "6:00 مساءً",
            feePerPlayer: data.feePerPlayer || 60,
            playersPerTeam: data.playersPerTeam || 2,
            welcomeText:
              data.welcomeText ||
              "متحمسين نشوفكم وتكونوا جزء من هالتحدي! فرصة حلوة تقضوا وقت ممتع وتتنافسوا على جوائز رهيبة!",
            visible: data.visible || false,
            registrationRules: data.registrationRules || [
              "يتثبت التسجيل فور وصول رسوم الاشتراك",
              "التسجيل متاح حتى اكتمال العدد المطلوب",
            ],
            tournamentRules: data.tournamentRules || [
              "الفريق صاحب أعلى نتيجة في كل جولة يتأهل إلى النهائي",
              "كل فريق سيشارك في جولة واحدة فقط ما عدا الجولة النهائية في حال التأهل",
              "يتنافس أفضل فرق في الجولة النهائية لتحديد الفائز",
              "يتم لعب جولة اضافية في حال التعادل",
              "يجب على اللاعبين انتظار دورهم واحترام حدود المسارات",
              "يجب قراءة والالتزام بالارشادات المتواجدة في منطقة البولينغ",
              "أي تصرف غير اخلاقي يؤدي إلى استبعاد اللاعب",
            ],
          };

          setTournamentSettings(settings);

          // Initialize form data with correct number of players
          setFormData(initializeFormData(settings.playersPerTeam));

          // Redirect if tournament is not visible
          if (!data.visible) {
            showError("التسجيل في البطولة غير متاح حالياً");
            navigate("/");
          }
        } else {
          // No settings found, redirect
          showError("التسجيل في البطولة غير متاح حالياً");
          navigate("/");
        }
      } catch (error) {
        console.error("Error loading tournament settings:", error);
        showError("حدث خطأ في تحميل إعدادات البطولة");
        navigate("/");
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [navigate, showError]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.teamName.trim()) {
      newErrors.teamName = "اسم الفريق مطلوب";
    }

    // Validate all player fields dynamically
    for (let i = 1; i <= tournamentSettings.playersPerTeam; i++) {
      if (!formData[`player${i}Name`]?.trim()) {
        newErrors[`player${i}Name`] = `اسم اللاعب ${
          i === 1
            ? "الأول"
            : i === 2
            ? "الثاني"
            : i === 3
            ? "الثالث"
            : i === 4
            ? "الرابع"
            : i
        } مطلوب`;
      }

      if (!formData[`player${i}Age`]?.trim()) {
        newErrors[`player${i}Age`] = `عمر اللاعب ${
          i === 1
            ? "الأول"
            : i === 2
            ? "الثاني"
            : i === 3
            ? "الثالث"
            : i === 4
            ? "الرابع"
            : i
        } مطلوب`;
      }
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = "رقم التواصل مطلوب";
    } else if (!/^[0-9+\-\s()]+$/.test(formData.contactNumber)) {
      newErrors.contactNumber = "يرجى إدخال رقم هاتف صحيح";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.howDidYouHear) {
      newErrors.howDidYouHear = "يرجى اختيار كيف سمعت عن البطولة";
    }

    if (!formData.agreement) {
      newErrors.agreement = "يجب الموافقة على الشروط والأحكام";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(1);
  };

  const validateForm = () => {
    return validateStep1() && validateStep2();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Add tournament registration to Firestore
      await addDoc(collection(db, "tournamentRegistrations"), {
        ...formData,
        registrationDate: serverTimestamp(),
        status: "pending",
        paymentStatus: "pending",
        totalFee:
          tournamentSettings.feePerPlayer * tournamentSettings.playersPerTeam,
      });

      showSuccess("تم تسجيل فريقك بنجاح! سنتواصل معك قريباً لتأكيد التسجيل.");

      // Reset form and go back to terms
      setFormData(initializeFormData(tournamentSettings.playersPerTeam));
      setCurrentStep(1);
      setTermsAccepted(false);
      setShowForm(false);
    } catch (error) {
      console.error("Error registering for tournament:", error);
      showError("حدث خطأ أثناء التسجيل. يرجى المحاولة مرة أخرى.");
    }

    setLoading(false);
  };

  const handleTermsAccept = () => {
    setShowForm(true);
  };

  const handleBackToTerms = () => {
    setTermsAccepted(false);
    setShowForm(false);
    setCurrentStep(1);
    setFormData(initializeFormData(tournamentSettings.playersPerTeam));
    setErrors({});
  };

  // Helper function to get Arabic ordinal number
  const getArabicOrdinal = (num) => {
    const ordinals = {
      1: "الأول",
      2: "الثاني",
      3: "الثالث",
      4: "الرابع",
      5: "الخامس",
      6: "السادس",
      7: "السابع",
      8: "الثامن",
    };
    return ordinals[num] || `${num}`;
  };

  if (settingsLoading) {
    return (
      <div className="tournament-registration">
        <div className="tournament-container">
          <div className="loading">جاري التحميل...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-registration">
      <div className="tournament-container">
        <div className="tournament-header">
          <h1>🏆 التسجيل في {tournamentSettings.tournamentName}</h1>
        </div>

        <div className="tournament-content">
          {/* Tournament Info Sidebar */}
          <div className="tournament-info-sidebar">
            <div className="tournament-info">
              <div className="tournament-welcome-text">
                <h2>أهلاً وسهلاً فيكم! ⚡️</h2>
                <p>{tournamentSettings.welcomeText}</p>
              </div>
              <div className="tournament-info-item">
                <span className="tournament-info-icon">📅</span>
                <span>
                  تاريخ المسابقة: {tournamentSettings.date} (
                  {tournamentSettings.dayOfWeek})
                </span>
              </div>
              <div className="tournament-info-item">
                <span className="tournament-info-icon">⏰</span>
                <span>الساعة: {tournamentSettings.time}</span>
              </div>
              <div className="tournament-info-item">
                <span className="tournament-info-icon">💰</span>
                <span>
                  رسوم الاشتراك: {tournamentSettings.feePerPlayer} شيكل لكل لاعب
                </span>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="tournament-main-content-area">
            {!showForm ? (
              // Terms Section - Show First
              <div className="tournament-terms-section-full">
                <h3>الشروط والأحكام</h3>
                <div className="tournament-terms-content">
                  <h4>شروط التسجيل:</h4>
                  <ul>
                    <li>
                      يجب أن يتكون كل فريق من{" "}
                      {tournamentSettings.playersPerTeam} لاعبين
                    </li>
                    <li>
                      رسوم الاشتراك {tournamentSettings.feePerPlayer} شيكل لكل
                      لاعب
                    </li>
                    {tournamentSettings.registrationRules.map((rule, index) => (
                      <li key={index}>{rule}</li>
                    ))}
                  </ul>

                  <h4>قواعد المسابقة:</h4>
                  <ul>
                    {tournamentSettings.tournamentRules.map((rule, index) => (
                      <li key={index}>{rule}</li>
                    ))}
                  </ul>

                  <div className="tournament-terms-acceptance">
                    <label className="tournament-checkbox-label">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                      />
                      <span className="tournament-checkbox-text">
                        أؤكد أنني قرأت ووافقت على الالتزام بقواعد وإرشادات
                        المسابقة
                      </span>
                    </label>
                    <button
                      className="tournament-continue-btn"
                      onClick={handleTermsAccept}
                      disabled={!termsAccepted}
                    >
                      {termsAccepted
                        ? "متابعة التسجيل"
                        : "يرجى قبول الشروط أولاً"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Registration Form - Show After Terms Acceptance
              <div className="tournament-registration-form-container">
                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="tournament-form-header">
                    <button
                      type="button"
                      className="tournament-back-to-terms-btn"
                      onClick={handleBackToTerms}
                    >
                      ← العودة للشروط
                    </button>
                  </div>

                  <div className="tournament-form-progress">
                    <div
                      className={`tournament-progress-step ${
                        currentStep >= 1 ? "active" : ""
                      }`}
                    >
                      <span className="tournament-step-number">1</span>
                      <span className="tournament-step-label">
                        معلومات الفريق
                      </span>
                    </div>
                    <div
                      className={`tournament-progress-step ${
                        currentStep >= 2 ? "active" : ""
                      }`}
                    >
                      <span className="tournament-step-number">2</span>
                      <span className="tournament-step-label">
                        التأكيد والموافقة
                      </span>
                    </div>
                  </div>

                  {currentStep === 1 && (
                    <div className="tournament-form-step">
                      <div className="tournament-form-group">
                        <label htmlFor="teamName">اسم الفريق *</label>
                        <input
                          type="text"
                          id="teamName"
                          name="teamName"
                          value={formData.teamName}
                          onChange={handleInputChange}
                          placeholder="أدخل اسم الفريق"
                          className={errors.teamName ? "error" : ""}
                          disabled={loading}
                        />
                        {errors.teamName && (
                          <span className="tournament-error-message">
                            {errors.teamName}
                          </span>
                        )}
                      </div>

                      {/* Dynamically render player fields */}
                      {Array.from(
                        { length: tournamentSettings.playersPerTeam },
                        (_, i) => i + 1
                      ).map((playerNum) => (
                        <div
                          key={playerNum}
                          className="tournament-players-section"
                        >
                          <h4>اللاعب {getArabicOrdinal(playerNum)}</h4>
                          <div className="tournament-player-fields">
                            <div className="tournament-form-group">
                              <label htmlFor={`player${playerNum}Name`}>
                                الاسم الكامل *
                              </label>
                              <input
                                type="text"
                                id={`player${playerNum}Name`}
                                name={`player${playerNum}Name`}
                                value={formData[`player${playerNum}Name`] || ""}
                                onChange={handleInputChange}
                                placeholder="أدخل الاسم الكامل"
                                className={
                                  errors[`player${playerNum}Name`]
                                    ? "error"
                                    : ""
                                }
                                disabled={loading}
                              />
                              {errors[`player${playerNum}Name`] && (
                                <span className="tournament-error-message">
                                  {errors[`player${playerNum}Name`]}
                                </span>
                              )}
                            </div>

                            <div className="tournament-form-group">
                              <label htmlFor={`player${playerNum}Age`}>
                                العمر *
                              </label>
                              <input
                                type="text"
                                id={`player${playerNum}Age`}
                                name={`player${playerNum}Age`}
                                value={formData[`player${playerNum}Age`] || ""}
                                onChange={handleInputChange}
                                placeholder="ادخل عمرك"
                                className={
                                  errors[`player${playerNum}Age`] ? "error" : ""
                                }
                                disabled={loading}
                              />
                              {errors[`player${playerNum}Age`] && (
                                <span className="tournament-error-message">
                                  {errors[`player${playerNum}Age`]}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="tournament-form-group">
                        <label htmlFor="contactNumber">رقم للتواصل *</label>
                        <input
                          type="tel"
                          id="contactNumber"
                          name="contactNumber"
                          value={formData.contactNumber}
                          onChange={handleInputChange}
                          placeholder="أدخل رقم الهاتف"
                          className={errors.contactNumber ? "error" : ""}
                          disabled={loading}
                        />
                        {errors.contactNumber && (
                          <span className="tournament-error-message">
                            {errors.contactNumber}
                          </span>
                        )}
                      </div>

                      <div className="tournament-form-actions">
                        <button
                          type="button"
                          className="tournament-next-btn"
                          onClick={handleNext}
                          disabled={loading}
                        >
                          التالي
                        </button>
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="tournament-form-step">
                      <div className="tournament-form-group">
                        <label>كيف سمعت عن بطولة ايجنايت للبولنغ؟ *</label>
                        <div className="tournament-radio-group">
                          {[
                            {
                              value: "social-media",
                              label: "وسائل التواصل الاجتماعي",
                            },
                            {
                              value: "ignite-team",
                              label: "فريق العمل في ايجنايت",
                            },
                            { value: "friend-family", label: "صديق او عائلة" },
                            { value: "other", label: "أخرى" },
                          ].map((option) => (
                            <label
                              key={option.value}
                              className="tournament-radio-option"
                            >
                              <input
                                type="radio"
                                name="howDidYouHear"
                                value={option.value}
                                checked={
                                  formData.howDidYouHear === option.value
                                }
                                onChange={handleInputChange}
                                disabled={loading}
                              />
                              <span className="tournament-radio-label">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                        {errors.howDidYouHear && (
                          <span className="tournament-error-message">
                            {errors.howDidYouHear}
                          </span>
                        )}
                      </div>

                      <div className="tournament-form-group">
                        <label className="tournament-checkbox-label">
                          <input
                            type="checkbox"
                            name="agreement"
                            checked={formData.agreement}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <span className="tournament-checkbox-text">
                            أؤكد أنني قرأت ووافقت على الالتزام بقواعد وإرشادات
                            المسابقة *
                          </span>
                        </label>
                        {errors.agreement && (
                          <span className="tournament-error-message">
                            {errors.agreement}
                          </span>
                        )}
                      </div>

                      <div className="tournament-form-actions">
                        <button
                          type="button"
                          className="tournament-prev-btn"
                          onClick={handlePrevious}
                          disabled={loading}
                        >
                          السابق
                        </button>
                        <button
                          type="submit"
                          className="tournament-submit-btn"
                          disabled={loading}
                        >
                          {loading ? "جاري التسجيل..." : "تسجيل الفريق"}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentRegistration;
